import React, { useEffect, useState } from 'react';
import PipelineShell from '../common/PipelineShell';
import useStageJob from '../common/useStageJob';
import AgentActivityGraph from '../common/AgentActivityGraph';
import {
  getProject,
  setStageData,
  patchStage,
  subscribe,
  buildStageContext,
} from '../../../services/pipelineProject.service';
import { runPipelineStage } from '../../../services/pipeline.service';
import '../Pipeline.css';

/**
 * Lightweight markdown renderer — handles the subset the LLM generates:
 * headings, bold, inline code, bullet lists, numbered lists, tables, fenced code blocks.
 * No npm dependency required.
 */
function MarkdownView({ content }) {
  if (!content) return null;

  const lines = content.split('\n');
  const elements = [];
  let i = 0;

  const parseInline = (text) => {
    // Bold, inline code
    const parts = text.split(/(`[^`]+`|\*\*[^*]+\*\*)/g);
    return parts.map((p, idx) => {
      if (p.startsWith('`') && p.endsWith('`')) return <code key={idx} style={{ background: '#f1f5f9', padding: '1px 5px', borderRadius: 3, fontFamily: 'monospace', fontSize: '0.88em' }}>{p.slice(1, -1)}</code>;
      if (p.startsWith('**') && p.endsWith('**')) return <strong key={idx}>{p.slice(2, -2)}</strong>;
      return p;
    });
  };

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block
    if (line.startsWith('```')) {
      const codeLines = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) { codeLines.push(lines[i]); i++; }
      elements.push(<pre key={i} style={{ background: '#0f172a', color: '#e2e8f0', padding: '12px 16px', borderRadius: 8, overflowX: 'auto', fontSize: '0.82em', margin: '8px 0' }}>{codeLines.join('\n')}</pre>);
      i++; continue;
    }

    // Table row
    if (line.startsWith('|')) {
      const rows = [];
      while (i < lines.length && lines[i].startsWith('|')) {
        if (!/^[|\s:-]+$/.test(lines[i])) rows.push(lines[i]);
        i++;
      }
      const isHead = rows.length > 0;
      elements.push(
        <table key={i} style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88em', margin: '8px 0' }}>
          <tbody>
            {rows.map((r, ri) => {
              const cells = r.split('|').slice(1, -1);
              return (
                <tr key={ri} style={{ background: ri === 0 && isHead ? '#f8fafc' : 'transparent' }}>
                  {cells.map((c, ci) => (
                    ri === 0 && isHead
                      ? <th key={ci} style={{ border: '1px solid #e2e8f0', padding: '6px 10px', textAlign: 'left', fontWeight: 600 }}>{parseInline(c.trim())}</th>
                      : <td key={ci} style={{ border: '1px solid #e2e8f0', padding: '6px 10px' }}>{parseInline(c.trim())}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      );
      continue;
    }

    // Headings
    const hMatch = line.match(/^(#{1,4})\s+(.*)/);
    if (hMatch) {
      const level = hMatch[1].length;
      const sizes = { 1: '1.4em', 2: '1.15em', 3: '1em', 4: '0.95em' };
      const weights = { 1: 700, 2: 650, 3: 600, 4: 600 };
      elements.push(<div key={i} style={{ fontSize: sizes[level], fontWeight: weights[level], margin: level === 1 ? '16px 0 8px' : '12px 0 4px', borderBottom: level <= 2 ? '1px solid #e2e8f0' : 'none', paddingBottom: level <= 2 ? 4 : 0, color: '#0f172a' }}>{parseInline(hMatch[2])}</div>);
      i++; continue;
    }

    // Bullet list item
    if (/^[-*]\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*]\s/.test(lines[i])) { items.push(lines[i].replace(/^[-*]\s/, '')); i++; }
      elements.push(<ul key={i} style={{ margin: '4px 0 8px 20px', padding: 0 }}>{items.map((it, idx) => <li key={idx} style={{ marginBottom: 3 }}>{parseInline(it)}</li>)}</ul>);
      continue;
    }

    // Numbered list item
    if (/^\d+\.\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) { items.push(lines[i].replace(/^\d+\.\s/, '')); i++; }
      elements.push(<ol key={i} style={{ margin: '4px 0 8px 20px', padding: 0 }}>{items.map((it, idx) => <li key={idx} style={{ marginBottom: 3 }}>{parseInline(it)}</li>)}</ol>);
      continue;
    }

    // 4-space indented block (code without fences)
    if (line.startsWith('    ') && line.trim()) {
      const codeLines = [];
      while (i < lines.length && (lines[i].startsWith('    ') || lines[i] === '')) { codeLines.push(lines[i].replace(/^    /, '')); i++; }
      elements.push(<pre key={i} style={{ background: '#1e293b', color: '#e2e8f0', padding: '10px 14px', borderRadius: 6, fontFamily: 'monospace', fontSize: '0.82em', margin: '6px 0', overflowX: 'auto' }}>{codeLines.join('\n').trimEnd()}</pre>);
      continue;
    }

    // Blank line
    if (!line.trim()) { elements.push(<div key={i} style={{ height: 6 }} />); i++; continue; }

    // Paragraph
    elements.push(<p key={i} style={{ margin: '4px 0', lineHeight: 1.6 }}>{parseInline(line)}</p>);
    i++;
  }

  return <div style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: '0.9rem', color: '#1e293b', lineHeight: 1.7, padding: '8px 0' }}>{elements}</div>;
}

/**
 * Docs Stage
 *
 * Generates documentation files: README, API Docs.
 * Each file is shown in a tab rendered with MarkdownView.
 */
function DocsStage() {
  // eslint-disable-next-line no-unused-vars
  const [project, setProject] = useState(getProject());
  const [files, setFiles]     = useState([]);
  const [activeFile, setActiveFile] = useState(0);
  const [error, setError]     = useState(null);
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    const p = getProject();
    if (p.stages?.docs?.data?.files) {
      setFiles(p.stages.docs.data.files);
    }
    return subscribe(setProject);
  }, []);

  const handleResult = (result) => {
    const docFiles = result?.files || [];
    setFiles(docFiles);
    setStageData('docs', { files: docFiles });
    setError(null);
  };

  const handleActivity = (acts) => setActivity(acts || []);
  const { polling, error: pollError, startPolling } = useStageJob(handleResult, setError, handleActivity);

  const handleGenerate = async () => {
    setError(null);
    const context = buildStageContext('docs');
    const comment = project?.stages?.docs?.comment || '';
    try {
      const res = await runPipelineStage('docs', context, comment);
      if (res.status === 'accepted') {
        patchStage('docs', { jobId: res.jobId });
        startPolling(res.jobId);
      } else if (res.status === 'completed') {
        handleResult(res.result);
        if (res.agentActivity) setActivity(res.agentActivity);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to generate documentation.');
    }
  };

  const design       = project?.stages?.design?.data;
  const hasFiles     = files.length > 0;
  const displayError = error || pollError;

  return (
    <PipelineShell
      stageId="docs"
      description="Generate comprehensive project documentation from the approved design."
      canApprove={hasFiles}
      headerAction={
        <button
          className="pipeline-btn primary"
          onClick={handleGenerate}
          disabled={polling || !design}
          title={!design ? 'Complete the Design stage first' : undefined}
        >
          {polling ? '⏳ Generating…' : hasFiles ? '↩ Regenerate' : '📚 Generate Docs'}
        </button>
      }
    >
      {!design && (
        <div className="pipeline-empty">
          <div className="pipeline-empty-icon">🔒</div>
          <p>Complete and approve the <strong>Design</strong> stage first.</p>
        </div>
      )}

      {(polling || activity.length > 0) && (
        <AgentActivityGraph activity={activity} polling={polling} />
      )}

      {polling && (
        <div className="pipeline-loading">
          <div className="pipeline-spinner" />
          <p>Generating documentation…</p>
        </div>
      )}

      {displayError && !polling && <div className="pipeline-error">{displayError}</div>}

      {hasFiles && !polling && (
        <div className="pipeline-card" style={{ padding: 0, overflow: 'hidden' }}>
          {/* File tabs */}
          <div className="pipeline-tabs" style={{ padding: '0 16px' }}>
            {files.map((f, i) => (
              <button
                key={f.name}
                className={`pipeline-tab-btn${activeFile === i ? ' active' : ''}`}
                onClick={() => setActiveFile(i)}
              >
                📄 {f.name}
              </button>
            ))}
          </div>
          {/* Content */}
          <div style={{ padding: '0 16px 16px' }}>
            {files[activeFile] && (
              <MarkdownView content={files[activeFile].content} />
            )}
          </div>
        </div>
      )}
    </PipelineShell>
  );
}

export default DocsStage;
